import { useState } from "react";

const SUBMIT_ADDR = "https://readdy.ai/api/form/db93u5bk13ra8mig913g";

export default function ContactForm() {
  const [status, setStatus] = useState<"idle" | "success" | "error">("idle");
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get("website_alt") ?? "").trim();
    if (honeypot) {
      setStatus("success");
      setMessage("感谢你的反馈，我们已收到你的留言。");
      form.reset();
      return;
    }
    formData.delete("website_alt");

    const email = String(formData.get("email") ?? "").trim();
    const name = String(formData.get("name") ?? "").trim();
    const content = String(formData.get("message") ?? "").trim();

    if (!name || !email || !content) {
      setStatus("error");
      setMessage("请填写称呼、邮箱和留言内容后再提交。");
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setStatus("error");
      setMessage("请填写正确的邮箱地址。");
      return;
    }
    if (content.length > 500) {
      setStatus("error");
      setMessage("留言内容请控制在 500 字以内。");
      return;
    }

    setSubmitting(true);
    setStatus("idle");
    setMessage("");

    try {
      const body = new URLSearchParams();
      formData.forEach((value, key) => {
        body.append(key, String(value));
      });

      const response = await fetch(SUBMIT_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });

      const responseText = await response.text();
      let parsed: {
        code?: string;
        message?: string;
        meta?: { message?: string; detail?: string };
      } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
      const isSpam = typeof serverMsg === "string" && serverMsg.toLowerCase().includes("spam");

      if (response.ok && parsed?.code === "OK" && !isSpam) {
        setStatus("success");
        setMessage("提交成功，感谢你的反馈，我们会尽快与你联系。");
        form.reset();
      } else {
        setStatus("error");
        setMessage(
          serverMsg && typeof serverMsg === "string"
            ? `提交失败：${serverMsg}`
            : "提交失败，请稍后重试。",
        );
      }
    } catch {
      setStatus("error");
      setMessage("网络异常，提交失败，请稍后重试。");
    } finally {
      setSubmitting(false);
    }
  };

  const inputClass =
    "h-11 w-full rounded-md border border-background-300 bg-background-50 px-3 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-secondary-400";

  return (
    <form
      data-readdy-form
      onSubmit={handleSubmit}
      className="rounded-lg border border-background-200 bg-background-50 p-6"
    >
      <h2 className="font-heading text-lg font-bold text-foreground-900">联系方式与反馈</h2>
      <p className="mt-1 text-sm text-foreground-500">
        有任何建议或合作意向，欢迎通过下方表单给我们留言。
      </p>

      <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div>
          <label htmlFor="contact-name" className="mb-1.5 block text-sm text-foreground-600">
            你的称呼
          </label>
          <input
            id="contact-name"
            name="name"
            type="text"
            placeholder="请输入称呼"
            className={inputClass}
          />
        </div>
        <div>
          <label htmlFor="contact-email" className="mb-1.5 block text-sm text-foreground-600">
            邮箱
          </label>
          <input
            id="contact-email"
            name="email"
            type="email"
            placeholder="请输入邮箱"
            className={inputClass}
          />
        </div>
      </div>

      <div className="mt-4">
        <label htmlFor="contact-topic" className="mb-1.5 block text-sm text-foreground-600">
          反馈类型
        </label>
        <select id="contact-topic" name="topic" defaultValue="内容建议" className={inputClass}>
          <option value="内容建议">内容建议</option>
          <option value="内容纠错">内容纠错</option>
          <option value="合作洽谈">合作洽谈</option>
          <option value="其他问题">其他问题</option>
        </select>
      </div>

      <div className="mt-4">
        <label htmlFor="contact-message" className="mb-1.5 block text-sm text-foreground-600">
          留言内容（500 字以内）
        </label>
        <textarea
          id="contact-message"
          name="message"
          rows={5}
          maxLength={500}
          placeholder="请输入你的建议或问题"
          className="w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-secondary-400"
        ></textarea>
      </div>

      <input
        type="text"
        name="website_alt"
        className="contact-ref-field"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        readOnly
      />

      {status !== "idle" && (
        <div
          className={`mt-4 rounded-md px-3 py-2 text-sm ${
            status === "success"
              ? "bg-secondary-50 text-secondary-700"
              : "bg-accent-50 text-accent-700"
          }`}
        >
          {message}
        </div>
      )}

      <button
        type="submit"
        disabled={submitting}
        className="mt-5 flex items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-600 px-6 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-60"
      >
        {submitting ? (
          <>
            <i className="ri-loader-4-line animate-spin"></i>
            提交中
          </>
        ) : (
          <>
            <i className="ri-send-plane-line"></i>
            提交反馈
          </>
        )}
      </button>
    </form>
  );
}